/**
 * gestures.ts — Logic thuần của cử chỉ cảm ứng trên điện thoại (có test): vuốt hàng để lộ thao tác, kéo xuống để làm mới,
 * vuốt từ mép trái để mở ngăn kéo bộ lọc.
 * Hook dùng chúng: hooks/use-swipe-reveal.ts, hooks/use-pull-to-refresh.ts, hooks/use-edge-swipe.ts.
 */

/** Ngưỡng (px) để coi là có ý định vuốt — dưới ngưỡng vẫn là chạm. */
export const SWIPE_INTENT_PX = 8

/**
 * Hướng vuốt sau khi ngón tay đi đủ xa: 'x' (ngang → kéo hàng), 'y' (dọc → để trình duyệt cuộn danh sách), null (chưa đủ xa).
 * Ngang chỉ thắng khi rõ ràng hơn dọc (×1.2) — cuộn hơi chéo không được kéo nhầm hàng.
 */
export function swipeAxis(dx: number, dy: number, threshold = SWIPE_INTENT_PX): 'x' | 'y' | null {
  const ax = Math.abs(dx)
  const ay = Math.abs(dy)
  if (ax < threshold && ay < threshold) return null
  return ax > ay * 1.2 ? 'x' : 'y'
}

/** Vị trí hàng khi kéo: bắt đầu từ `base` (0 = đóng, -max = đang mở), kẹp trong [-max - kéo quá chút, 0]. */
export function clampSwipe(base: number, dx: number, max: number): number {
  const next = base + dx
  if (next > 0) return 0
  // Cho kéo quá mép mở một chút (có lực cản) cho cảm giác tự nhiên rồi bật lại.
  if (next < -max) return -max + (next + max) * 0.25
  return next
}

/** Thả tay: mở hẳn (-max) nếu đã kéo quá nửa hoặc vuốt nhanh sang trái; ngược lại đóng (0). */
export function settleSwipe(offset: number, max: number, velocityPxPerMs = 0): number {
  if (velocityPxPerMs < -0.5) return -max
  if (velocityPxPerMs > 0.5) return 0
  return offset <= -max / 2 ? -max : 0
}

/** Khoảng kéo xuống hiển thị (có lực cản 0.5, tối đa `max`). Âm / 0 → 0. */
export function pullDistance(dy: number, max = 96, resistance = 0.5): number {
  if (dy <= 0) return 0
  return Math.min(max, dy * resistance)
}

/** Đủ xa để làm mới khi thả tay. */
export function shouldRefresh(pull: number, threshold = 64): boolean {
  return pull >= threshold
}

// ─────────────────────────────────────────────────────────────────────────────
// Vuốt từ MÉP TRÁI màn hình để mở ngăn kéo (Hộp thư di động)
// ─────────────────────────────────────────────────────────────────────────────

/** Vùng mép trái (px) mà ngón tay phải chạm xuống mới tính là "vuốt từ mép". Hẹp để không cướp cú chạm vào avatar / hàng. */
export const EDGE_SWIPE_ZONE_PX = 24
/** Ngón tay phải đi sang phải tối thiểu bấy nhiêu px (và rõ ràng nằm ngang) thì ngăn kéo mở. */
export const EDGE_SWIPE_OPEN_PX = 56

/** Điểm chạm xuống có nằm trong vùng mép trái không (x tính từ mép trái cửa sổ). */
export function inEdgeZone(x: number, zone = EDGE_SWIPE_ZONE_PX): boolean {
  return x >= 0 && x <= zone
}

/**
 * Diễn biến của một cú vuốt đã bắt đầu ở mép trái, tính theo độ dời (dx, dy) từ điểm chạm:
 * 'open' = đủ xa sang phải và nằm ngang → mở ngăn kéo; 'cancel' = hướng dọc hoặc sang trái → bỏ (để cuộn / vuốt hàng chạy bình thường);
 * 'pending' = chưa đủ để quyết định.
 */
export function edgeSwipeState(dx: number, dy: number, openPx = EDGE_SWIPE_OPEN_PX): 'pending' | 'open' | 'cancel' {
  const axis = swipeAxis(dx, dy)
  if (axis === 'y') return 'cancel'
  if (axis === 'x' && dx < 0) return 'cancel'
  if (axis === 'x' && dx >= openPx) return 'open'
  return 'pending'
}

export interface EdgeSwipeEnv {
  /** Thiết bị Apple chạy iOS / iPadOS. */
  ios: boolean
  /** Đang chạy như ứng dụng đã cài (display-mode: standalone / navigator.standalone). */
  standalone: boolean
}

/**
 * Có nên bật cử chỉ vuốt mép trái không. iOS đã cài ra màn hình chính (standalone): mép trái là cử chỉ "quay lại" của hệ điều hành,
 * trang web không nhận được cú vuốt (hoặc nhận rồi bị huỷ) → tắt, người dùng dùng nút lọc. Các trường hợp còn lại bật
 * (trình duyệt iOS / Android; Android dùng điều hướng cử chỉ vẫn có thể chiếm mép — xem ghi chú trong nhật ký).
 */
export function edgeSwipeSupported(env: EdgeSwipeEnv): boolean {
  return !(env.ios && env.standalone)
}

/** Nhận diện iOS / iPadOS từ chuỗi user-agent (iPadOS 13+ khai là "Macintosh" nhưng có cảm ứng nhiều điểm). */
export function isIosDevice(ua: string, maxTouchPoints = 0): boolean {
  if (/iPad|iPhone|iPod/.test(ua)) return true
  return /Macintosh/.test(ua) && maxTouchPoints > 1
}
